/* eslint-disable i18next/no-literal-string */
import { FeatureFlag, ReportMetric } from '@cbo/shared-library';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import * as yup from 'yup';
import { isEqual } from 'lodash';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormContainer } from 'react-hook-form-mui';
import { useForm } from 'react-hook-form';
import { DashboardMetricConfiguration } from '@cbo/shared-library/request/admin.request';
import { useUsers } from '../../contexts/userContext';
import { useGetDashboardGraphData, useGetKPIData } from '../../../api/sales/queries';
import { DashboardGraphDataResponse, ReportMetricResult } from '../../../api/sales/requests';
import { useUserDashboardConfigurationDataQuery } from '../requests/queries';
import { KPIMappedResultConfigs, defaultKPIData, mapKPIResponse } from './DashboardUtils';
import ScorecardContainer from './ScorecardContainer/ScorecardContainer';
import ComparisonRange from '../../config/comparisonRange';
import DateFilter from '../../components/GlobalFilterBar/DateFilter/DateFilter';
import { GlobalFilterBarDateInput } from '../../components/GlobalFilterBar/types';
import { today } from '../../utils/reportingUtils/filterUtils';
import useYupValidationResolver from '../../utils/formUtils/yupValidationResolver';
import { usePackages } from '../../contexts/packageContext';
import { useFeatureFlags } from '../../utils/hooks/useFeatureFlag';
import routes from '../../constants/routes';
import useCurrentSite from '../../utils/hooks/useCurrentSite';

// 2 minutes
const REFRESH_TIMER = 120000;

function DashboardPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useUsers();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery('(max-width:374px)');
  const { currentSite: selectedSite } = useCurrentSite();
  const [currentSelectedEnterpriseUnitId, setCurrentSelectedEnterpriseUnitId] = useState<string | undefined>(undefined);
  const { data: dashboardConfigurationData, isLoading: isDashboardConfigurationLoading } =
    useUserDashboardConfigurationDataQuery(user.fullyAuthenticated === 'authenticated');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const refreshIntervalRef: MutableRefObject<any> = useRef(0);
  const [isSessionActive] = useState(sessionStorage.getItem('isSessionActive'));
  const [dashboardConfiguration, setDashboardConfiguration] = useState<DashboardMetricConfiguration[] | undefined>(
    undefined
  );
  const [kpiMappedResults, setKPIMappedResults] = useState<KPIMappedResultConfigs | undefined>(undefined);
  const [netSalesInfo, setNetSalesInfo] = useState<{ reportMetricResult: ReportMetricResult; url: string } | undefined>(
    undefined
  );
  const [graphData, setGraphData] = useState<DashboardGraphDataResponse | undefined>(undefined);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [dateRangeQueryParam, setDateRangeQueryParam] = useState<string>('');

  const { kpiData, setRequest, isKPIDataLoading } = useGetKPIData();
  const { graphDataResponse, setGraphRequest, isGraphDataLoading } = useGetDashboardGraphData();

  const lastFetchedSiteId = useRef<string | undefined>(undefined);

  const [cboForecastLink, cboGuestCheckLink, cboHideAlohaEsentialOrgs] = useFeatureFlags(
    FeatureFlag.laborForecastLink,
    FeatureFlag.laborGuestCheckLink,
    FeatureFlag.hideAlohaEssentialOrgs
  );
  const { starter } = usePackages();

  const defaultDateRange: GlobalFilterBarDateInput = {
    startDate: today,
    endDate: today,
  };
  const [selectedFilters, setSelectedFilters] = useState<GlobalFilterBarDateInput>();
  const formContext = useForm({
    mode: 'onChange',
    resolver: useYupValidationResolver(
      yup.object({
        dateRange: yup.object().shape({ startDate: yup.date().required(), endDate: yup.date().required() }),
      })
    ),
    defaultValues: {
      dateRange: defaultDateRange,
    },
  });
  const { watch } = formContext;
  const dateRange = watch('dateRange');

  useEffect(() => {
    const siteChanged = selectedSite.enterpriseUnitId !== lastFetchedSiteId.current;
    if (
      (!isEqual(selectedFilters, dateRange) || siteChanged) &&
      !user.isOrgSwitching &&
      dashboardConfiguration &&
      !isDashboardConfigurationLoading &&
      user.bslAuth?.sites &&
      Object.values(user.bslAuth.sites).some((site) => site.enterpriseUnitId === selectedSite.enterpriseUnitId)
    ) {
      const metrics = dashboardConfiguration.map((config) => config.metric);
      setRequest({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        metricsRequested: metrics,
      });
      setGraphRequest({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      setDateRangeQueryParam(`?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
      setSelectedFilters(dateRange);
      lastFetchedSiteId.current = selectedSite.enterpriseUnitId;
    }
  }, [
    dateRange,
    setRequest,
    setGraphRequest,
    dashboardConfiguration,
    user.bslAuth?.sites,
    user.isOrgSwitching,
    isDashboardConfigurationLoading,
    selectedSite.enterpriseUnitId,
    refresh,
    selectedFilters,
  ]);

  useEffect(() => {
    if (!kpiData) {
      return;
    }

    const forecastDataExists = !!kpiData.reportMetricResults[ReportMetric.FORECAST_SALES];
    const shouldIncludeForecast = cboForecastLink && forecastDataExists;

    // Always map KPI data so the dashboard can render even if forecast is missing.
    const filteredKpiData = shouldIncludeForecast
      ? kpiData
      : {
          ...kpiData,
          reportMetricResults: Object.fromEntries(
            Object.entries(kpiData.reportMetricResults).filter(([key]) => key !== ReportMetric.FORECAST_SALES)
          ),
        };

    // dashboardConfiguration can never not be set here. Reasoning is in the useEffect above
    setKPIMappedResults(mapKPIResponse(filteredKpiData, dashboardConfiguration ?? []));

    if (filteredKpiData.reportMetricResults[ReportMetric.NET_SALES]) {
      setNetSalesInfo({
        reportMetricResult: filteredKpiData.reportMetricResults[ReportMetric.NET_SALES],
        url: dashboardConfigurationData?.metrics?.length
          ? dashboardConfigurationData.metrics[0].url + dateRangeQueryParam
          : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpiData, isKPIDataLoading, dateRangeQueryParam, currentSelectedEnterpriseUnitId]);

  useEffect(() => {
    if (graphDataResponse) {
      let actualSalesSum = 0;
      let comparisonSalesSum = 0;
      let forecastedSalesSum = 0;
      const actualSales = graphDataResponse.netSales.map((value) => {
        actualSalesSum += value;
        return actualSalesSum;
      });
      const comparisonSales = graphDataResponse.comparisonNetSales.map((value) => {
        comparisonSalesSum += value;
        return comparisonSalesSum;
      });
      const forecastedSales = graphDataResponse.forecastedNetSales.map((value) => {
        forecastedSalesSum += value;
        return forecastedSalesSum;
      });
      setGraphData({
        ...graphDataResponse,
        netSales: actualSales,
        comparisonNetSales: comparisonSales,
        forecastedNetSales: forecastedSales,
      });
    }
  }, [graphDataResponse, isGraphDataLoading]);

  useEffect(() => {
    if (dashboardConfigurationData?.metrics && !isDashboardConfigurationLoading) {
      const sortedConfiguration: DashboardMetricConfiguration[] | undefined = dashboardConfigurationData.metrics;

      const filterOut = !starter ? [ReportMetric.LABOR_PERCENTAGE] : [];

      const filtered =
        filterOut.length < 1
          ? sortedConfiguration
          : sortedConfiguration?.filter((config) => !filterOut.includes(config.metric));
      const finalConfiguration = !cboForecastLink
        ? filtered?.filter((config) => config.metric !== ReportMetric.FORECAST_SALES)
        : filtered;

      setDashboardConfiguration(finalConfiguration);
    }
  }, [dashboardConfigurationData, isDashboardConfigurationLoading, starter, cboForecastLink]);

  useEffect(() => {
    let resetKpiMap = false;
    if (selectedSite && selectedSite.enterpriseUnitId !== currentSelectedEnterpriseUnitId) {
      setCurrentSelectedEnterpriseUnitId(selectedSite.enterpriseUnitId);
      resetKpiMap = true;
    }

    if (!isEqual(selectedFilters, dateRange) && selectedSite) {
      resetKpiMap = true;
      clearInterval(refreshIntervalRef.current);

      if (today === dateRange.startDate || today === dateRange.endDate) {
        refreshIntervalRef.current = setInterval(() => {
          setRefresh((toggleRefresh) => !toggleRefresh);
        }, REFRESH_TIMER);
      }
    }

    if (resetKpiMap) {
      setKPIMappedResults(undefined);
    }

    return () => {
      clearInterval(refreshIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedSite.enterpriseUnitId]);

  useEffect(() => {
    if (!isSessionActive) sessionStorage.setItem('isSessionActive', 'true');
  }, [isSessionActive]);

  const disableUrlBasedOnFeatureFlag = (url: string): [string | undefined, boolean | undefined] => {
    let finalUrl: string | undefined;
    let isUrlLinked: boolean | undefined;

    const featureFlags = [
      {
        flag: cboForecastLink && !cboHideAlohaEsentialOrgs,
        url: routes.INTERVAL_SALES_AND_LABOR,
      },
      {
        flag: cboGuestCheckLink && !cboHideAlohaEsentialOrgs,
        url: routes.EMPLOYEE_SALES_PERFORMANCE,
      },
      {
        flag: !cboHideAlohaEsentialOrgs,
        url: routes.SALES_TRANSACTION_DETAILS_REPORT,
      },
    ];

    for (let i = 0; i < featureFlags.length; i += 1) {
      if (
        (featureFlags[i].flag && url.includes(featureFlags[i].url)) ||
        (!url.includes(routes.INTERVAL_SALES_AND_LABOR) &&
          !url.includes(routes.EMPLOYEE_SALES_PERFORMANCE) &&
          !url.includes(routes.SALES_TRANSACTION_DETAILS_REPORT))
      ) {
        finalUrl = url;
        isUrlLinked = starter;
        break;
      } else {
        finalUrl = undefined;
        isUrlLinked = false;
      }
    }

    return [finalUrl, isUrlLinked];
  };

  return (
    <Box
      sx={{
        height: '100%',
        paddingX: isMobile ? 2 : 4,
        paddingY: 2,
      }}
      data-testid='dashboard-page'
      display='block'
    >
      {/* Date range filter */}
      <Box
        sx={{
          display: 'flex',
          marginBottom: 4,
          width: isMobile ? '100%' : 'auto',
        }}
        data-testid='date-range-picker'
      >
        <FormContainer formContext={formContext}>
          <DateFilter controllerName='dateRange' onlyFilterContainer={false} predefined />
        </FormContainer>
      </Box>

      {/* Scorecards */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: isSmallMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '24px',
        }}
        data-testid='dashboard-scorecards'
      >
        {!!dashboardConfiguration &&
          dashboardConfiguration.map((config: DashboardMetricConfiguration, i: number) => {
            if (
              cboHideAlohaEsentialOrgs &&
              (config.metric === ReportMetric.LABOR_PERCENTAGE || config.metric === ReportMetric.SALES_PER_LABOR_HOUR)
            ) {
              return null;
            }
            const metricConfig = kpiMappedResults?.[config.metric];
            const [finalUrl, isUrlLinked] = disableUrlBasedOnFeatureFlag((config?.url ?? '') + dateRangeQueryParam);
            return (
              <ScorecardContainer
                key={config.order}
                scaleStyle={config.style}
                singleValueCardProps={{
                  title: t(metricConfig?.title ?? defaultKPIData.title),
                  value: metricConfig?.value ?? defaultKPIData.value,
                  url: finalUrl,
                  loading: !kpiMappedResults,
                  currency: metricConfig?.currency ?? defaultKPIData.currency,
                  percentage: metricConfig?.percentage ?? defaultKPIData.percentage,
                  positiveResultsPreferred: metricConfig?.positiveResultsPreferred,
                  comparisonRange: ComparisonRange.YEAR,
                  comparisonValue: metricConfig?.change ?? defaultKPIData.comparisonValue,
                  isClickable: isUrlLinked,
                }}
                lineChartProps={{
                  singleValueProps: {
                    title: t('admin.dashboardPage.KPICards.netSales'),
                    value: netSalesInfo?.reportMetricResult.currentValue ?? 0,
                    comparisonValue: netSalesInfo?.reportMetricResult.change ?? 0,
                    comparisonRange: ComparisonRange.YEAR,
                    positiveResultsPreferred: true,
                    currency: true,
                  },
                  url: netSalesInfo?.url ?? '',
                  loading: !graphData || !kpiMappedResults,
                  isSameDay: dateRange.startDate === dateRange.endDate,
                  data: {
                    xAxis: graphData?.xAxis ?? [],
                    netSales: graphData?.netSales ?? [],
                    comparisonNetSales: graphData?.comparisonNetSales ?? [],
                    forecastedNetSales: graphData?.forecastedNetSales ?? [],
                  },
                }}
              />
            );
          })}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '142px',
            width: isSmallMobile ? '100%' : '185px',
            overflow: 'clip',
            flex: isMobile ? '40%' : 'none',
          }}
          hidden
        />
      </Box>
    </Box>
  );
}

export default DashboardPage;
